<template>
    <div class="flex justify-center mt-10">
        <!-- Primary Button -->
        <button 
            @click="showSelects = true" 
            v-if="!showSelects" 
            class="bg-orange p-5 text-white rounded flex items-center justify-center hover:bg-orange-dark"
            id="reapply-button"
        >
            <span>{{ buttonText }}</span>
        </button>

        <div v-if="showSelects" class="flex flex-col items-center space-y-2 w-full">
            <div v-if="errors.global" class="mb-2">
                <span class="text-danger">{{ errors.global }}</span>
            </div>

            <div class="flex flex-col w-full max-w-[300px]">
            
                <div class="flex-1 mb-4">
                    <select 
                        id="select-amount"
                        v-model="amount" 
                        required
                        class="border border-gray-300 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="" disabled selected>Select Amount:</option>
                        <option v-for="(amount, key) in getDefaultAmounts" :value="key" :key="key">${{ amount }}</option>
                    </select>
                    <span class="text-danger">{{ errors.amount }}</span>
                </div>

                <div class="flex-1 mb-4">
                    <select 
                        id="select-term"
                        v-model="term"
                        required
                        class="border border-gray-300 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="" disabled selected>Payback period:</option>
                        <option value="6">6 months</option>
                        <option value="12">12 months</option>
                        <option value="24">24 months</option>
                        <option value="36">36 months</option>
                    </select>
                    <span class="text-danger">{{ errors.term }}</span>
                </div>

                <div class="flex-1 mb-4">
                    <select 
                        id="select-frequency"
                        required
                        v-model="frequency"
                        class="border border-gray-300 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="" disabled selected>Select Frequency:</option>
                        <option value="bi-weekly">Bi-Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="twice_monthly">Twice Monthly</option>
                    </select>
                    <span class="text-danger">{{ errors.frequency }}</span>
                </div>

            </div>

            <!-- Payment Day Selects - Only show when frequency is twice_monthly -->
            <div v-if="frequency === 'twice_monthly'" class="flex flex-col w-full max-w-[300px]">
                <div class="flex-1 mb-4">
                    <select 
                        id="select-first-payment-day"
                        v-model="first_payment_day"
                        required
                        class="border border-gray-300 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="" disabled selected>First Pay Day</option>
                        <option v-for="day in 30" :value="day" :key="day">{{ day }}</option>
                    </select>
                    <span class="text-danger">{{ errors.first_payment_day }}</span>
                </div>
                <div class="flex-1 mb-4">
                    <select 
                        id="select-second-payment-day"
                        v-model="second_payment_day"
                        required
                        class="border border-gray-300 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="" disabled selected>Second Pay Day</option>
                        <option v-for="day in 30" :value="day" :key="day">{{ day }}</option>
                    </select>
                    <span class="text-danger">{{ errors.second_payment_day }}</span>
                </div>
            </div>

            <!-- Cancel and Submit Buttons -->
            <div class="flex space-x-4 mt-4">
                <button 
                    @click="cancelReApply" 
                    class="bg-gray-300 p-3 text-black rounded hover:bg-gray-400"
                >
                    Cancel
                </button>
                <button 
                    :disabled="loading"
                    @click="reApply" 
                    class="bg-orange p-3 text-white rounded flex items-center justify-center hover:bg-orange-dark disabled:bg-gray-400 disabled:cursor-not-allowed"
                    id="submit-button"
                >
                    <svg 
                        v-if="loading" 
                        class="animate-spin h-5 w-5 text-white mr-2" 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24"
                    >
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <span v-if="!loading">Submit</span>
                    <span v-else>Processing...</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'ReApplyButton',

    props: {
        loan: {
            type: Object,
            required: true
        },
        buttonText: {
            type: String,
            default: 'One Click Re-Apply'
        },
        redirectAfterSuccess: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            loading: false,
            showSelects: false,
            unknownError: 'An unexpected error occurred. Please try again later.',
            errors: {
                amount: null,
                term: null,
                frequency: null,
                first_payment_day: null,
                second_payment_day: null,
                global: null,
            },
            amount: "",
            term: "",
            frequency: "",
            first_payment_day: "",
            second_payment_day: "",
        }
    },

    watch: {
        frequency(newValue) {
            // Clear payment day values when frequency changes from twice_monthly
            if (newValue !== 'twice_monthly') {
                this.first_payment_day = "";
                this.second_payment_day = "";
                // Clear any existing errors for payment days
                this.errors.first_payment_day = null;
                this.errors.second_payment_day = null;
            }
        }
    },

    computed: {
        ...mapGetters([
            'getAmountByState'
        ]),

        getDefaultAmounts() {
            let amounts = this.getAmountByState();
            return amounts;
        }
    },

    methods: {
        resetForm() {
            this.amount = "";
            this.term = "";
            this.frequency = "";
            this.first_payment_day = "";
            this.second_payment_day = "";
            this.errors = {
                amount: null,
                term: null,
                frequency: null,
                first_payment_day: null,
                second_payment_day: null,
                global: null,
            };
        },

        cancelReApply() {
            this.showSelects = false;
            this.resetForm();
            this.$emit('cancelled');
        },

        reApply() {
            this.resetErrors();
            this.loading = true;
            
            // Prepare request data
            const requestData = {
                amount: this.amount,
                term: this.term,
                frequency: this.frequency
            };

            // Add payment days only if frequency is twice_monthly
            if (this.frequency === 'twice_monthly') {
                requestData.first_payment_day = this.first_payment_day;
                requestData.second_payment_day = this.second_payment_day;
            }

            // Emit event before starting the request
            this.$emit('reapply-started', requestData);
            
            setTimeout(() => {
                axios.post('/account/loans/' + this.loan.id + '/re-apply', requestData)
                .then(response => {
                    console.log(response.data);
                    
                    // Emit success event
                    this.$emit('reapply-success', response.data);
                    
                    // Reset form
                    this.resetForm();
                    this.showSelects = false;
                    
                    // Redirect if enabled
                    if (this.redirectAfterSuccess) {
                        window.location.href = '/account/loans/' + response.data.loan_id;
                    }
                    
                }).catch(error => {
                    this.handleError(error);
                    
                    // Emit error event
                    this.$emit('reapply-error', error);
                    
                }).finally(() => {
                    this.loading = false;
                });
            }, 1000);
        },

        resetErrors() {
            this.errors = {
                amount: null,
                term: null,
                frequency: null,
                first_payment_day: null,
                second_payment_day: null,
                global: null,
            };
        },

        handleError(error) {
            // Check if the error status is 500
            if (error.response && error.response.status === 500) {
                this.errors.global = error.response.data.message || this.unknownError;
            } else if (error.response && error.response.data && error.response.data.errors) {
                // Handle validation errors (typically 422)
                let hasFieldError = false;
                for (const key in error.response.data.errors) {
                    if (this.errors.hasOwnProperty(key)) {
                        this.errors[key] = error.response.data.errors[key][0];
                        hasFieldError = true;
                    }
                }
                // Show as global error if no matching field (e.g. loan_id errors)
                if (!hasFieldError && error.response.data.message) {
                    this.errors.global = error.response.data.message;
                }
            } else {
                this.errors.global = this.unknownError;
            }
        }
    }
}
</script>

<style scoped>
/* Orange color scheme for buttons */
.bg-orange {
    background-color: #e69e4f; /* Custom orange */
}

.bg-orange:hover {
    background-color: #d18a39; /* Darker shade for hover */
}

.bg-orange-dark {
    background-color: #d18a39; /* Darker shade */
}

.bg-orange:disabled {
    background-color: #f2c18a; /* Lighter orange for disabled */
    cursor: not-allowed;
    opacity: 0.6; /* Optional: adds transparency for better disabled visual */
}

/* Existing styles */
.text-danger {
    color: #dc3545;
}

/* Hover effects for cancel button */
.hover\:bg-gray-400:hover {
    background-color: #9ca3af;
}

/* Updated disabled state utility to use lighter orange */
.disabled\:bg-gray-400:disabled {
    background-color: #f2c18a; /* Light orange instead of gray */
}

.disabled\:cursor-not-allowed:disabled {
    cursor: not-allowed;
}
</style>